import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, Check, Star } from "lucide-react";
import simpleIcon from "@/assets/simple_iamerica.png";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import SubscriptionEmailDialog from "@/components/SubscriptionEmailDialog";
import PurchaseChoiceDialog from "@/components/PurchaseChoiceDialog";
import { useVideoSubscriptionPlans, type VideoSubscriptionPlan } from "@/hooks/useVideoSubscriptionPlans";
import { useDisplayCurrency, resolvePlanPrice, resolveCompareAtPrice, formatPlanPrice } from "@/hooks/useDisplayCurrency";

type PaidPlanSlug = string;

const Pricing = () => {
  const { t, i18n } = useTranslation("landing");
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const isEs = i18n.language?.startsWith("es");
  const { plans, loading: plansLoading, error: plansError, refetch: refetchPlans } = useVideoSubscriptionPlans();

  // AI tutor allowances per plan (anon-readable RPC; empty pre-migration).
  const [tutorOffers, setTutorOffers] = useState<Record<string, { video_minutes: number; questions: number; chat_messages: number; storage_days: number }>>({});
  useEffect(() => {
    (async () => {
      try {
        const { data } = await (supabase as any).rpc("tutor_plan_offers");
        if (Array.isArray(data)) {
          setTutorOffers(Object.fromEntries(data.map((r: any) => [r.plan_slug, r])));
        }
      } catch { /* pricing works without tutor lines */ }
    })();
  }, []);

  const tutorLines = (slug: string) => {
    const o = tutorOffers[slug];
    if (!o) return null;
    const lines =
      slug === "free"
        ? [
            o.video_minutes > 0
              ? t("pricing.tutor.free_minutes", "Try the AI Tutor: {{count}} free video minutes", { count: o.video_minutes })
              : t("pricing.tutor.no_free_minutes", "AI Tutor video minutes are not included on the Free plan"),
            t("pricing.tutor.chat", "{{count}} AI chat messages with Alonso / month", { count: o.chat_messages }),
          ]
        : [
            t("pricing.tutor.minutes", "AI Tutor: {{count}} video minutes of classes / month", { count: o.video_minutes }),
            t("pricing.tutor.questions", "{{count}} questions to your tutor / month", { count: o.questions }),
            t("pricing.tutor.storage", "Your classes saved for {{count}} days", { count: o.storage_days }),
            t("pricing.tutor.chat", "{{count}} AI chat messages with Alonso / month", { count: o.chat_messages }),
          ];
    return (
      <>
        <li className="pt-2 mt-1 border-t border-border/60">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-[#4169B8]">
            {t("pricing.tutor.header", "AI Tutor included")}
          </span>
        </li>
        {lines.map((l, i) => featureRow(l, 100 + i))}
      </>
    );
  };
  const currency = useDisplayCurrency();

  const scrollRef = useRef<HTMLDivElement>(null);
  const [loadingSlug, setLoadingSlug] = useState<PaidPlanSlug | null>(null);
  const [emailGate, setEmailGate] = useState<{ open: boolean; plan: PaidPlanSlug }>({
    open: false,
    plan: "monthly",
  });
  const [choiceGate, setChoiceGate] = useState<{ open: boolean; plan: PaidPlanSlug }>({
    open: false,
    plan: "monthly",
  });

  // Edge function currently accepts "monthly" or "annual". Other slugs are reserved for future use.
  const isCheckoutSupported = (slug: string) => slug === "monthly" || slug === "annual";

  const startCheckout = async (
    plan: PaidPlanSlug,
    email?: string,
    giftRecipientEmail?: string,
  ) => {
    try {
      setLoadingSlug(plan);
      const body: Record<string, any> = { plan, currency };
      try { const { getStoredRef } = await import("@/lib/referralStorage"); const r = getStoredRef(); if (r) body.referral_code = r; } catch {}
      if (email) body.email = email;
      if (giftRecipientEmail) body.gift_recipient_email = giftRecipientEmail;
      const { data, error } = await supabase.functions.invoke("create-all-access-checkout", {
        body,
      });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data?.error || "Could not start checkout");
      }
    } catch (e: any) {
      toast({
        title: t("pricing.error_title"),
        description: e.message || t("pricing.error_desc"),
        variant: "destructive",
      });
      setLoadingSlug(null);
    }
  };

  const handleAllAccess = async (plan: PaidPlanSlug) => {
    if (user) {
      // Logged-in users get the for-me / gift choice dialog
      setChoiceGate({ open: true, plan });
      return;
    }
    setEmailGate({ open: true, plan });
  };

  const handleEmailSubmitted = async (email: string) => {
    const plan = emailGate.plan;
    setEmailGate({ open: false, plan });
    await startCheckout(plan, email);
  };

  const handleChoiceConfirmed = async (
    mode: "self" | "gift",
    recipientEmail?: string,
  ) => {
    const plan = choiceGate.plan;
    setChoiceGate({ open: false, plan });
    await startCheckout(plan, undefined, mode === "gift" ? recipientEmail : undefined);
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.innerWidth >= 768) return;
    if (!scrollRef.current) return;
    if (plansLoading || plans.length === 0) return;
    const el = scrollRef.current;
    const cards = el.children;
    const popularCard = cards[1] as HTMLElement | undefined;
    if (popularCard) {
      el.scrollTo({ left: popularCard.offsetLeft, behavior: "auto" });
    }
  }, [plansLoading, plans.length]);

  // Free tier CTA → opens our free "Introduction to AI" lesson directly.
  const INTRO_AI_VIDEO_ID = "c9f48b77-0123-468c-b412-03da89ce2214";
  const handleStartFree = () => {
    navigate(`/videos?course=${INTRO_AI_VIDEO_ID}`);
  };

  const featureRow = (text: string, idx: number) => (
    <li key={idx} className="flex items-start gap-2 text-sm">
      <Check className="h-4 w-4 text-[#fcb424] mt-0.5 shrink-0" />
      <span>{text}</span>
    </li>
  );

  const localized = (en: string | null, es: string | null) =>
    (isEs ? (es || en) : en) || "";

  const formatPrice = (p: VideoSubscriptionPlan) => {
    const { amount, currency: c } = resolvePlanPrice(p, currency);
    return formatPlanPrice(amount, c);
  };

  const intervalLabel = (p: VideoSubscriptionPlan) => {
    if (p.interval === "one_time") return "";
    if (p.interval === "month") return `/${t("pricing.per_month")}`;
    if (p.interval === "year") return `/${t("pricing.per_year")}`;
    return "";
  };

  const renderPaidCard = (plan: VideoSubscriptionPlan, idx: number) => {
    const features = (isEs ? plan.features_es : plan.features) as string[];
    const ctaLabel =
      localized(plan.cta_label, plan.cta_label_es) ||
      t("pricing.monthly.cta");
    const tagline = localized(plan.tagline, plan.tagline_es);
    const isPopular = plan.is_popular;
    const supported = isCheckoutSupported(plan.slug);

    const compareAt = resolveCompareAtPrice(plan, currency);
    const current = resolvePlanPrice(plan, currency);
    const hasDiscount = !!compareAt && compareAt.amount > current.amount;
    const discountPct = hasDiscount
      ? Math.round(((compareAt!.amount - current.amount) / compareAt!.amount) * 100)
      : 0;

    return (
      <Card
        key={plan.id}
        className={
          "flex flex-col relative w-full md:w-auto " +
          (isPopular
            ? "border-2 border-[#fcb424] md:scale-[1.02] shadow-lg"
            : "border-border/60")
        }
      >
        {isPopular && (
          <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#fcb424] text-[#070e1e] hover:bg-[#fcb424] gap-1">
            <Star className="h-3 w-3 fill-current" />
            {tagline || t("pricing.most_popular")}
          </Badge>
        )}
        {!isPopular && tagline && (
          <Badge
            className={
              "absolute -top-3 right-4 text-white " +
              (hasDiscount
                ? "bg-rose-600 hover:bg-rose-600"
                : "bg-emerald-500 hover:bg-emerald-500")
            }
          >
            {hasDiscount ? `${tagline} · −${discountPct}%` : tagline}
          </Badge>
        )}
        <CardContent className="p-4 md:p-5 flex flex-col flex-1">
          <h3 className="text-xl font-semibold">{localized(plan.name, plan.name_es)}</h3>
          <div className="mt-2 md:mt-3 flex items-baseline gap-2 flex-wrap">
            <span className="text-4xl font-bold">{formatPlanPrice(current.amount, current.currency)}</span>
            <span className="text-muted-foreground">{intervalLabel(plan)}</span>
            {hasDiscount && (
              <span className="text-base text-muted-foreground line-through">
                {formatPlanPrice(compareAt!.amount, compareAt!.currency)}
              </span>
            )}
          </div>
          {plan.description && (
            <p className="text-sm text-muted-foreground mt-1">
              {localized(plan.description, plan.description_es)}
            </p>
          )}
          <ul className="mt-3 md:mt-4 space-y-1.5 flex-1">
            {features.map((f, i) => featureRow(f, i))}
            {tutorLines(plan.slug)}
          </ul>
          <Button
            onClick={() => handleAllAccess(plan.slug)}
            disabled={loadingSlug === plan.slug || !supported}
            size="lg"
            className={
              "mt-4 md:mt-5 w-full font-semibold " +
              (isPopular
                ? "bg-[#fcb424] text-[#070e1e] hover:bg-[#fcb424]/90"
                : "bg-[#070e1e] text-white hover:bg-[#070e1e]/90")
            }
          >
            {loadingSlug === plan.slug ? "..." : ctaLabel}
          </Button>
          {!supported && (
            <p className="text-[11px] text-muted-foreground mt-2 text-center">
              {t("pricing.coming_soon")}
            </p>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <section id="pricing" className="w-full pt-2 pb-4 md:pt-1 md:pb-4 bg-muted/30 scroll-mt-24">
      <div className="container mx-auto px-4 lg:px-6">
        <div className="max-w-2xl mx-auto text-center mb-4 md:mb-6">
          <h2 className="text-xl sm:text-2xl md:text-4xl font-bold tracking-tight flex items-center justify-center gap-2 md:gap-3">
            <img
              src={simpleIcon}
              alt=""
              aria-hidden="true"
              className="w-8 h-8 md:w-[55px] md:h-[55px] object-contain"
            />
            {t("pricing.title")}
          </h2>
          <p className="mt-3 text-muted-foreground text-lg">{t("pricing.subtitle")}</p>
        </div>

        <div ref={scrollRef} className="flex flex-col md:grid scrollbar-hide md:grid-cols-3 gap-5 max-w-6xl mx-auto items-stretch py-2 md:px-0 md:py-0">
          {/* Card 1 — Free (hardcoded; not a paid plan) */}
          <Card className="flex flex-col border-border/60 w-full md:w-auto">
            <CardContent className="p-4 md:p-5 flex flex-col flex-1">
              <h3 className="text-xl font-semibold">{t("pricing.free.name")}</h3>
              <div className="mt-2 md:mt-3 flex items-baseline gap-1">
                <span className="text-4xl font-bold">$0</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">{t("pricing.free.subtitle")}</p>
              <ul className="mt-3 md:mt-4 space-y-1.5 flex-1">
                {featureRow(t("pricing.free.f1"), 0)}
                {featureRow(t("pricing.free.f2"), 1)}
                {featureRow(t("pricing.free.f3"), 2)}
                {featureRow(t("pricing.free.f4"), 3)}
                {tutorLines("free")}
              </ul>
              <Button
                onClick={handleStartFree}
                size="lg"
                variant="outline"
                className="mt-4 md:mt-5 w-full"
              >
                {t("pricing.free.cta")}
              </Button>
            </CardContent>
          </Card>

          {/* Paid plans loaded from DB */}
          {plansLoading ? (
            Array.from({ length: 2 }).map((_, i) => (
                <Card key={i} className="flex flex-col border-border/60 w-full md:w-auto">
                  <CardContent className="p-4 md:p-5 space-y-4">
                    <Skeleton className="h-6 w-1/2" />
                    <Skeleton className="h-10 w-1/3" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-10 w-full mt-4" />
                  </CardContent>
                </Card>
              ))
          ) : plansError || plans.length === 0 ? (
            <Card className="md:col-span-2 flex flex-col border-destructive/30 bg-destructive/5 w-full md:w-auto">
              <CardContent className="p-4 md:p-5 flex flex-col items-center justify-center text-center min-h-[260px]">
                <AlertCircle className="h-10 w-10 text-destructive mb-3" />
                <h3 className="text-xl font-semibold text-foreground">
                  {t("pricing.plans_unavailable_title", "Subscription plans could not load")}
                </h3>
                <p className="text-sm text-muted-foreground mt-2 max-w-md">
                  {t(
                    "pricing.plans_unavailable_desc",
                    "Monthly and Annual All-Access plans are temporarily unavailable. Please try again.",
                  )}
                </p>
                {plansError && (
                  <p className="mt-3 text-xs text-destructive break-words max-w-md">
                    {plansError}
                  </p>
                )}
                <Button
                  type="button"
                  variant="outline"
                  className="mt-5"
                  onClick={() => refetchPlans()}
                >
                  {t("pricing.retry", "Retry")}
                </Button>
              </CardContent>
            </Card>
          ) : (
            plans.map((p, i) => renderPaidCard(p, i))
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-3 md:mt-5 max-w-2xl mx-auto">
          {t("pricing.fine_print", { currency })}
        </p>
      </div>
      <SubscriptionEmailDialog
        open={emailGate.open}
        plan={emailGate.plan as "monthly" | "annual"}
        loading={loadingSlug !== null}
        onOpenChange={(o) => setEmailGate((s) => ({ ...s, open: o }))}
        onSubmit={handleEmailSubmitted}
      />
      <PurchaseChoiceDialog
        open={choiceGate.open}
        onOpenChange={(o) => setChoiceGate((s) => ({ ...s, open: o }))}
        productType="subscription"
        productName={choiceGate.plan === "annual" ? t("pricing.annual_name") : t("pricing.monthly_name")}
        loading={loadingSlug !== null}
        onConfirm={handleChoiceConfirmed}
      />
    </section>
  );
};

export default Pricing;
